/**
 * KnowledgeBaseEditPage — edit a KB article in the Success & Support pane.
 *
 * Loads article via GET /api/kb/articles/:id/admin, saves via PATCH /api/kb/articles/:id.
 * Content is BlockNote JSON; edited through MarthaEditor.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import {
  ChevronLeft,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Loader2,
  Clock,
  FileText,
  Globe,
  Hash,
  Layers,
  ChevronDown,
} from 'lucide-react';
import { kbApi } from '../../../api/kb';
import MarthaEditor from '../../../components/editor/MarthaEditor';

// ── Status config ─────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  draft: {
    label: 'Draft',
    pill: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
  },
  published: {
    label: 'Published',
    pill: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
  },
  archived: {
    label: 'Archived',
    pill: 'bg-neutral-100 text-neutral-500 border-neutral-200 hover:bg-neutral-200',
  },
} as const;

type ArticleStatus = keyof typeof STATUS_CONFIG;
const STATUS_ORDER: ArticleStatus[] = ['draft', 'published', 'archived'];

// ── Skeleton ──────────────────────────────────────────────────────────────────

function EditSkeleton() {
  return (
    <div className="flex flex-col h-screen bg-white animate-pulse">
      {/* Header skeleton */}
      <div className="h-14 border-b border-neutral-100 flex items-center px-4 gap-3 shrink-0">
        <div className="h-7 w-7 rounded-md bg-neutral-100" />
        <div className="h-5 w-64 rounded bg-neutral-100" />
        <div className="ml-auto flex items-center gap-2">
          <div className="h-6 w-20 rounded-full bg-neutral-100" />
          <div className="h-8 w-16 rounded-lg bg-neutral-100" />
        </div>
      </div>
      {/* Body skeleton */}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 px-12 py-8 space-y-3">
          <div className="h-4 w-3/4 rounded bg-neutral-100" />
          <div className="h-4 w-full rounded bg-neutral-100" />
          <div className="h-4 w-5/6 rounded bg-neutral-100" />
          <div className="h-4 w-2/3 rounded bg-neutral-100" />
        </div>
        <div className="w-56 border-l border-neutral-100 p-4 space-y-4 shrink-0">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-3 w-16 rounded bg-neutral-100" />
              <div className="h-4 w-full rounded bg-neutral-100" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function KnowledgeBaseEditPage() {
  const { wsSlug, id } = useParams<{ wsSlug: string; id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // ── Remote data ──
  const { data: article, isLoading, isError, refetch } = useQuery({
    queryKey: ['kb-article-edit', id],
    queryFn: () => kbApi.getArticleForEdit(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  // ── Draft state ──
  const [draftTitle, setDraftTitle] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [draftStatus, setDraftStatus] = useState<ArticleStatus>('draft');
  const [statusOpen, setStatusOpen] = useState(false);
  const statusRef = useRef<HTMLDivElement>(null);

  // Saved baseline (to compute dirty)
  const savedTitle = useRef('');
  const savedContent = useRef('');
  const savedStatus = useRef<ArticleStatus>('draft');

  // Feedback
  const [saveMsg, setSaveMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const saveMsgTimer = useRef<ReturnType<typeof setTimeout>>();

  // Sync draft state when article loads
  useEffect(() => {
    if (!article) return;
    const st = (article.status ?? 'draft') as ArticleStatus;
    setDraftTitle(article.title ?? '');
    setDraftContent(article.content ?? '');
    setDraftStatus(STATUS_ORDER.includes(st) ? st : 'draft');
    savedTitle.current = article.title ?? '';
    savedContent.current = article.content ?? '';
    savedStatus.current = STATUS_ORDER.includes(st) ? st : 'draft';
  }, [article]);

  // Close status dropdown on outside click
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) {
        setStatusOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isDirty =
    draftTitle !== savedTitle.current ||
    draftContent !== savedContent.current ||
    draftStatus !== savedStatus.current;

  // ── Save mutation ──
  const saveMutation = useMutation({
    mutationFn: () =>
      kbApi.updateArticle(id!, {
        title: draftTitle,
        content: draftContent,
        status: draftStatus,
      }),
    onSuccess: () => {
      savedTitle.current = draftTitle;
      savedContent.current = draftContent;
      savedStatus.current = draftStatus;
      queryClient.invalidateQueries({ queryKey: ['kb-articles'] });
      clearTimeout(saveMsgTimer.current);
      setSaveMsg({ type: 'ok', text: 'Saved' });
      saveMsgTimer.current = setTimeout(() => setSaveMsg(null), 2500);
    },
    onError: (err: Error) => {
      clearTimeout(saveMsgTimer.current);
      setSaveMsg({ type: 'err', text: err.message ?? 'Save failed' });
    },
  });

  const handleContentChange = useCallback((content: string) => {
    setDraftContent(content);
  }, []);

  const handleSave = () => {
    if (saveMutation.isPending) return;
    saveMutation.mutate();
  };

  // ── Keyboard shortcut Cmd/Ctrl+S ──
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  // ── States ──
  if (isLoading) return <EditSkeleton />;

  if (isError || !article) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-white gap-4">
        <AlertCircle size={32} className="text-red-400" />
        <p className="text-sm text-neutral-600 font-medium">Failed to load article</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
        >
          Retry
        </button>
      </div>
    );
  }

  const statusCfg = STATUS_CONFIG[draftStatus] ?? STATUS_CONFIG.draft;
  const liveUrl =
    savedStatus.current === 'published' && article.slug
      ? `https://support.heyarchie.com/kb/${article.slug}`
      : null;

  return (
    <div className="flex flex-col h-screen bg-white overflow-hidden">

      {/* ── Command bar ──────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 flex items-center gap-3 px-4 h-14 border-b border-neutral-100 bg-white/95 backdrop-blur-sm shrink-0">

        {/* Back */}
        <button
          onClick={() => navigate(`/w/${wsSlug}/su/kb`)}
          className="flex items-center justify-center h-8 w-8 rounded-md text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 transition-colors shrink-0"
          title="Back to Knowledge Base"
        >
          <ChevronLeft size={18} />
        </button>

        {/* Breadcrumb divider */}
        <span className="text-neutral-200 text-lg font-light select-none">/</span>

        {/* Editable title */}
        <input
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          className={`
            flex-1 min-w-0 text-[15px] font-semibold text-neutral-900 bg-transparent
            border-0 outline-none rounded px-1 py-0.5
            focus:bg-neutral-50 focus:ring-1 focus:ring-indigo-200
            transition-all placeholder:text-neutral-300
          `}
          placeholder="Article title…"
        />

        {/* Right side controls */}
        <div className="flex items-center gap-2 shrink-0 ml-2">

          {/* Dirty indicator */}
          {isDirty && !saveMutation.isPending && (
            <span className="flex items-center gap-1 text-[11px] text-neutral-400 font-medium">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-400 inline-block" />
              Unsaved
            </span>
          )}

          {/* Save feedback */}
          {saveMsg && (
            <span className={`flex items-center gap-1 text-[11px] font-medium ${
              saveMsg.type === 'ok' ? 'text-emerald-600' : 'text-red-500'
            }`}>
              {saveMsg.type === 'ok'
                ? <CheckCircle size={12} />
                : <AlertCircle size={12} />
              }
              {saveMsg.text}
            </span>
          )}

          {/* Status pill dropdown */}
          <div ref={statusRef} className="relative">
            <button
              onClick={() => setStatusOpen((v) => !v)}
              className={`
                inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border
                transition-colors ${statusCfg.pill}
              `}
            >
              {statusCfg.label}
              <ChevronDown size={11} className={`transition-transform ${statusOpen ? 'rotate-180' : ''}`} />
            </button>
            {statusOpen && (
              <div className="absolute right-0 top-full mt-1.5 w-36 bg-white border border-neutral-100 rounded-xl shadow-lg shadow-neutral-100/80 overflow-hidden z-50 py-1">
                {STATUS_ORDER.map((s) => {
                  const cfg = STATUS_CONFIG[s];
                  return (
                    <button
                      key={s}
                      onClick={() => { setDraftStatus(s); setStatusOpen(false); }}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors
                        ${draftStatus === s ? 'bg-neutral-50' : 'hover:bg-neutral-50'}
                      `}
                    >
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border ${cfg.pill.replace('hover:bg-amber-100','').replace('hover:bg-emerald-100','').replace('hover:bg-neutral-200','')}`}>
                        {cfg.label}
                      </span>
                      {draftStatus === s && <span className="ml-auto text-indigo-500">✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending || (!isDirty && !saveMsg)}
            className={`
              inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[13px] font-medium
              transition-all
              ${saveMutation.isPending
                ? 'bg-indigo-400 text-white cursor-wait'
                : isDirty
                ? 'bg-indigo-600 text-white hover:bg-indigo-700 active:bg-indigo-800 shadow-sm'
                : 'bg-neutral-100 text-neutral-400 cursor-default'
              }
            `}
          >
            {saveMutation.isPending
              ? <><Loader2 size={13} className="animate-spin" /> Saving…</>
              : 'Save'
            }
          </button>
        </div>
      </header>

      {/* ── Body ─────────────────────────────────────────────────────────────── */}
      <div className="flex flex-1 min-h-0">

        {/* Editor area */}
        <main className="flex-1 min-w-0 overflow-hidden">
          <div className="h-[calc(100vh-56px)]">
            <MarthaEditor
              key={article.id}
              initialContent={article.content}
              onChange={handleContentChange}
              features={{
                outline: false,
                comments: false,
                ai: false,
                mermaid: false,
                stats: false,
                breadcrumb: false,
              }}
            />
          </div>
        </main>

        {/* Metadata sidebar */}
        <aside className="w-56 shrink-0 border-l border-neutral-100 bg-neutral-50/50 overflow-y-auto">
          <div className="p-4 space-y-5">

            <p className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 mb-1">
              Article Info
            </p>

            {/* Collection */}
            <div className="space-y-1">
              <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                <Layers size={10} /> Collection
              </p>
              <p className="text-xs text-neutral-700 font-medium">
                {article.collection_name ?? (
                  <span className="text-neutral-400 italic">No collection</span>
                )}
              </p>
            </div>

            {/* Slug */}
            {article.slug && (
              <div className="space-y-1">
                <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                  <Hash size={10} /> Slug
                </p>
                <p className="text-[11px] text-neutral-500 font-mono break-all leading-relaxed">
                  {article.slug}
                </p>
              </div>
            )}

            {/* Doc type */}
            <div className="space-y-1">
              <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                <FileText size={10} /> Type
              </p>
              <p className="text-xs text-neutral-600 capitalize">{article.doc_type}</p>
            </div>

            {/* Last updated */}
            <div className="space-y-1">
              <p className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
                <Clock size={10} /> Updated
              </p>
              <p className="text-xs text-neutral-500">
                {formatDistanceToNow(new Date(article.updated_at), { addSuffix: true })}
              </p>
            </div>

            {/* View live */}
            {liveUrl && (
              <div className="pt-1">
                <a
                  href={liveUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 w-full px-3 py-2 rounded-lg text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 transition-colors"
                >
                  <Globe size={12} />
                  View live
                  <ExternalLink size={10} className="ml-auto opacity-60" />
                </a>
              </div>
            )}

            {/* Keyboard hint */}
            <div className="pt-2 border-t border-neutral-100">
              <p className="text-[10px] text-neutral-300 leading-relaxed">
                <kbd className="font-mono bg-neutral-100 text-neutral-400 px-1 py-0.5 rounded text-[9px]">
                  ⌘S
                </kbd>
                {' '}to save
              </p>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
